import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type {
  AddMarketHealthWatchlistItemInput,
  MarketDataPipeline,
  MarketHealthWatchlistItemDto,
} from 'shared';

import { apiClient } from '../../lib/api/client';

import { marketHealthKeys } from './queries';

async function addWatchlistItem(
  input: AddMarketHealthWatchlistItemInput,
): Promise<MarketHealthWatchlistItemDto> {
  const { data } = await apiClient.post<{ item: MarketHealthWatchlistItemDto }>(
    '/market-health/watchlist',
    input,
  );
  return data.item;
}

async function removeWatchlistItem(symbol: string): Promise<void> {
  await apiClient.delete(`/market-health/watchlist/${encodeURIComponent(symbol)}`);
}

export function useAddMarketHealthWatchlistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addWatchlistItem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketHealthKeys.all });
    },
  });
}

export function useRemoveMarketHealthWatchlistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeWatchlistItem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketHealthKeys.all });
    },
  });
}

async function refreshMarketDataPipeline(pipeline: MarketDataPipeline): Promise<void> {
  try {
    await apiClient.post(`/market-health/pipelines/${pipeline}/refresh`);
  } catch (error) {
    const message = axios.isAxiosError<{ error?: { message?: string } }>(error)
      ? error.response?.data?.error?.message
      : undefined;
    throw message ? new Error(message) : error;
  }
}

export function useRefreshMarketDataPipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refreshMarketDataPipeline,
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: marketHealthKeys.pipelines });
    },
  });
}
